import React, { useRef, useState } from 'react';
import { DocMentionPicker } from '../../../components/DocMentionPicker';
import { buildMentionHtml, replaceSlashWithMention } from '../../../utils/mentionUtils';
import { focusEditable } from '../../../utils/focusUtils';

/**
 * "/doc" mention picker. The slash menu opens this at the same caret spot;
 * picking a doc splices an atomic tag in place of `/query`.
 */
export const useDocMentionPicker = ({
  blockId,
  contentEditableRef,
  contentRef,
  setContent,
  onUpdate,
  availableDocs,
}) => {
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const slashTextNodeRef = useRef(null);
  const slashQueryLenRef = useRef(0);

  const closeDocPicker = () => {
    setShowDocPicker(false);
    slashTextNodeRef.current = null;
    focusEditable(blockId, { preventScroll: true });
  };

  const openDocPicker = ({ position: pos, textNode, queryLength } = {}) => {
    slashTextNodeRef.current = textNode || null;
    slashQueryLenRef.current = queryLength || 0;
    if (pos) setPosition(pos);
    setShowDocPicker(true);
  };

  const handleDocPick = (doc) => {
    const el = contentEditableRef.current;
    if (el) {
      const placed = replaceSlashWithMention(
        el,
        slashTextNodeRef.current,
        slashQueryLenRef.current,
        buildMentionHtml(doc)
      );
      if (!placed) {
        // Fallback (caret snapshot lost): append the tag at the end
        el.innerHTML = `${el.innerHTML} ${buildMentionHtml(doc)}`;
      }
      const value = el.innerHTML;
      contentRef.current = value;
      setContent(value);
      onUpdate(blockId, { content: value });
    }
    closeDocPicker();
  };

  const docPicker = showDocPicker ? (
    <DocMentionPicker
      position={position}
      onPick={handleDocPick}
      onClose={closeDocPicker}
      availableDocs={availableDocs}
    />
  ) : null;

  return { openDocPicker, docPicker };
};
